import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '../../shared/services/auth.services';
import { MenuService } from '../../shared/services/menu.service';
import { SimulationService } from '../../shared/services/simulation.service';
import { MenuItem } from '../../shared/components/menu/menu';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private menuService = inject(MenuService);
  private simulationService = inject(SimulationService);
  private router = inject(Router);
  private menuSubscription: Subscription | null = null;
  private simulationSubscription: Subscription | null = null;
  private isSimulating = false;

  nomeUsuario = '';
  primeiroNome = '';
  menuItems: MenuItem[] = [];
  allMenuItems: MenuItem[] = [];

  ngOnInit(): void {
    this.carregarDadosUsuario();
    this.carregarMenus();
  }

  ngOnDestroy(): void {
    if (this.menuSubscription) {
      this.menuSubscription.unsubscribe();
      this.menuSubscription = null;
    }
    if (this.simulationSubscription) {
      this.simulationSubscription.unsubscribe();
      this.simulationSubscription = null;
    }
  }

  private carregarDadosUsuario(): void {
    const userInfo = this.authService.getUserInfo();
    if (userInfo) {
      this.nomeUsuario = userInfo.nome;
      this.primeiroNome = userInfo.nome.split(' ')[0];
    }
  }

  private carregarMenus(): void {
    const menuSalvo = sessionStorage.getItem('menu');
    if (menuSalvo) {
      try {
        this.menuItems = JSON.parse(menuSalvo);
        this.allMenuItems = this.flattenMenuItems(this.menuItems);
      } catch {
        this.menuItems = [];
        this.allMenuItems = [];
      }
    }

    this.menuSubscription = this.menuService.menuSubject.subscribe({
      next: (menu: MenuItem[]) => {
        this.menuItems = menu;
        this.allMenuItems = this.flattenMenuItems(menu);
        this.aplicarEstadoSimulacao();
      },
    });

    this.simulationSubscription = this.simulationService.simulationState$.subscribe({
      next: (state) => {
        this.isSimulating = state.active;
        this.aplicarEstadoSimulacao();
      },
    });
  }

  // Rotas gerenciadas internamente por componentes agrupadores (ex: Relatórios)
  // que não devem aparecer como cards independentes na home
  private readonly rotasSubordinadas = [
    '/desligamento-profissional/relatorio',
    '/desligamento-profissional/relatorio/visualizar',
    '/relatorios/desligamento-profissional',
    '/relatorios/desligamento-profissional/visualizar',
    '/relatorios/gerencial',
    '/ajuda-custos/analisar',
    '/ajuda-custos/dashboard',
    '/ajuda-custos/pagamentos',
    '/ajuda-custos/lotes-pagamento',
    '/ajuda-custos/arquivos-retorno',
    '/recesso/parametrizar',
    '/recesso/solicitar',
    '/avaliacao-fenotipica',
    '/analise-documental-acoes-afirmativas',
  ];

  private flattenMenuItems(items: MenuItem[]): MenuItem[] {
    const result: MenuItem[] = [];

    const flatten = (menuItems: MenuItem[]) => {
      for (const item of menuItems) {
        const rota = item.route ?? item.url ?? '';
        if ((item.route || item.url) && !this.rotasSubordinadas.includes(rota)) {
          result.push(item);
        }
        if (item.children && item.children.length > 0) {
          flatten(item.children);
        }
      }
    };

    flatten(items);
    return result;
  }

  navegarPara(item: MenuItem): void {
    if (item.disabled) {
      return;
    }
    if (item.route) {
      this.router.navigate([item.route]);
    } else if (item.url) {
      window.open(item.url, '_blank', 'noopener');
    }
  }

  getCardLabel(item: MenuItem): string {
    const route = (item.route || item.url || '').toLowerCase();

    if (route.includes('/painel_maav')) {
      return 'MAAv - Participações Disponíveis';
    }

    if (route.includes('/maav/dashboard')) {
      return 'MAAv - Dashboard';
    }

    if (route.includes('/maav/enviar-email-lote')) {
      return 'MAAv - Enviar e-mail em lote';
    }

    if (route === '/maav' || route.endsWith('/maav')) {
      return 'MAAv - Gestão';
    }

    return item.label;
  }

  private aplicarEstadoSimulacao(): void {
    for (const item of this.allMenuItems) {
      if (item.route?.includes('simular-perfil')) {
        item.disabled = this.isSimulating;
      }
    }
  }

  logout(): void {
    this.authService.logout();
  }
}
